import { Button, Icon, Paper } from '@material-ui/core';
import { capitalizeAll, ModeTextInputField, useModeFormStyle } from '@moderepo/mode-app-components';
import clsx from 'clsx';
import produce from 'immer';
import { useCallback, useState } from 'react';
import { AlertRuleEditorStep, useEditAlertRuleStyle, ALERT_RULE_MESSAGING_TITLE_LENGTH_MAX, ALERT_RULE_MESSAGING_TITLE_LENGTH_MIN } from '.';
import { useRitzTranslation } from '../../../utils/customHooks';
import { useSelectProject } from '../../../utils/dataStateHooks';


/**
 * The structure for the Messages Step errors
 */
interface StepMessagesInputErrors {
    title?: string | undefined;
    recoveryTitle?: string | undefined;
}


/**
 * The required props for the EditAlertRuleStepMessages in addition to the props inherited from AlertRuleEditorStep
 */
export interface EditAlertRuleStepMessagesProps extends AlertRuleEditorStep {
    readonly title?: string | undefined;
    readonly recoveryTitle?: string | undefined;
    readonly onStepCompleted: (title: string, recoveryTitle?: string | undefined)=> void;
}


/**
 * This is the Edit Alert Rule Messages step which is the step which the user needs to provide the messages to be used for notifications
 */
export const EditAlertRuleStepMessages: React.FC<EditAlertRuleStepMessagesProps> = (props: EditAlertRuleStepMessagesProps) => {

    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const sharedClasses = useEditAlertRuleStyle();
    const formClasses = useModeFormStyle();

    const [titleInput, setTitleInput] = useState<string>(props.title ?? '');
    const [recoveryTitleInput, setRecoveryTitleInput] = useState<string>(props.recoveryTitle ?? '');
    const [inputErrors, setInputErrors] = useState<StepMessagesInputErrors>({});


    /**
     * Submit the user inputs for this step. Before submitting the inputs, we will validate the inputs. If there are no errors,
     * we will notify the parent container by calling ths 'onStepCompleted' callback and provide the parent container with the new
     * inputs and return 'true' for success. If there are errors, we will notify the parent container that there are errors but only
     * if 'dispatchOnError' is true and also return false for failure.
     */
    const submitInputs = useCallback((dispatchOnError: boolean = true): boolean => {

        const newInputErrors: StepMessagesInputErrors = { };
        const newTitle = titleInput.trim();
        const newRecoveryTitle = recoveryTitleInput.trim();

        // Validate the values and set the error if any of the input has error
        if (!newTitle) {
            newInputErrors.title = 'error.input.generic.empty';
        } else if (newTitle.length < ALERT_RULE_MESSAGING_TITLE_LENGTH_MIN || newTitle.length > ALERT_RULE_MESSAGING_TITLE_LENGTH_MAX) {
            newInputErrors.title = 'error.input.generic.range';
        }

        // recoveryTitle is OPTIONAL but if it is provided then it must be valid
        if (newRecoveryTitle && (newRecoveryTitle.length < ALERT_RULE_MESSAGING_TITLE_LENGTH_MIN
                || newRecoveryTitle.length > ALERT_RULE_MESSAGING_TITLE_LENGTH_MAX)) {
            newInputErrors.recoveryTitle = 'error.input.generic.range';
        }

        // Update the local inputErrors
        setInputErrors(newInputErrors);


        // If there is AT LEAST 1 field in the newInputError, this Step is considered to contain errors.
        const hasErrors = Object.values(newInputErrors).some((error) => {
            return error !== undefined;
        });


        // If 'dispatchOnError' is specified, dispatch the error or null to the container
        if (dispatchOnError) {
            props.onStepError(hasErrors);
        }


        // If there are no errors, notify the parent container this step is completed
        if (!hasErrors) {
            props.onStepCompleted(newTitle, newRecoveryTitle || undefined);
        }

        return !hasErrors;
    }, [titleInput, recoveryTitleInput, props]);


    return (
        <div className={sharedClasses.stepContentBody}>
            <div className={clsx(sharedClasses.stepContentForm)}>
                <div className={clsx(sharedClasses.stepSubtitle)}>
                    {trans('alerting.edit_alert_rule.step_messages.subtitle')}
                </div>
                <Paper elevation={1} className={clsx(sharedClasses.formControlGroup)}>
                    <ModeTextInputField
                        fieldName="title"
                        className={clsx(formClasses.formInputField, sharedClasses.formControl, 'title')}
                        required
                        editable
                        isInEditMode
                        hideEditableIcon
                        label={capitalizeAll(trans('alerting.message_template.title'))}
                        description={trans('alerting.message_template.title.hint')}
                        error={inputErrors.title ? trans(inputErrors.title, {
                            min: ALERT_RULE_MESSAGING_TITLE_LENGTH_MIN, max: ALERT_RULE_MESSAGING_TITLE_LENGTH_MAX,
                        }) : undefined}
                        value={titleInput || ''}
                        onInputChange={(fieldName, value) => {
                            // Input changed so clear the error and update the state
                            props.onStepError(false);
                            setInputErrors(produce(inputErrors, (draft: StepMessagesInputErrors) => {
                                delete draft.title;
                            }));
                            setTitleInput(value);
                        }}
                    />

                    <ModeTextInputField
                        fieldName="recoveryTitle"
                        className={clsx(formClasses.formInputField, sharedClasses.formControl, 'recovery-title')}
                        editable
                        isInEditMode
                        hideEditableIcon
                        label={capitalizeAll(trans('alerting.message_template.recovery_title'))}
                        description={trans('alerting.message_template.recovery_title.hint')}
                        error={inputErrors.recoveryTitle ? trans(inputErrors.recoveryTitle, {
                            min: ALERT_RULE_MESSAGING_TITLE_LENGTH_MIN, max: ALERT_RULE_MESSAGING_TITLE_LENGTH_MAX,
                        }) : undefined}
                        value={recoveryTitleInput || ''}
                        onInputChange={(fieldName, value) => {
                            // Input changed so clear the error and update the state
                            props.onStepError(false);
                            setInputErrors(produce(inputErrors, (draft: StepMessagesInputErrors) => {
                                delete draft.recoveryTitle;
                            }));
                            setRecoveryTitleInput(value);
                        }}
                    />
                </Paper>

                <div className={sharedClasses.templateMessageNote}>
                    {trans('alerting.message_template.note', { interpolation: { skipOnVariables: true } })}
                </div>
            </div>

            <div className={sharedClasses.stepContentActionsContainer}>
                <div className={sharedClasses.stepContentRightActionsContainer}>
                    <Button
                        disabled={!props.canGoBack}
                        onClick={() => {
                            // Validate the inputs AND dispatch onComplete if successful so that we can save the user inputs before going back.
                            // However, if inputs are invalid, we will not dispatch error and just take the user back to the previous step. We will
                            // not save the user's input since it is invalid anyway.
                            props.onStepError(false);
                            submitInputs(false);
                            props.onPrevStepClick();
                        }}
                        className={sharedClasses.navButton}
                        startIcon={<Icon>navigate_before</Icon>}
                    >
                        {capitalizeAll(trans('label.back'))}
                    </Button>
                    <Button
                        onClick={() => {
                            if (submitInputs()) {
                                props.onNextStepClick();
                            }
                        }}
                        className={sharedClasses.navButton}
                        endIcon={<Icon>navigate_next</Icon>}
                    >
                        {capitalizeAll(trans(props.isLastStep ? 'label.finish' : 'label.next'))}
                    </Button>
                    {!props.isNewAlertRule && (
                        <Button
                            variant="contained"
                            color="default"
                            size="small"
                            onClick={() => {
                                if (props.onGotoLastStepClick && submitInputs()) {
                                    props.onGotoLastStepClick();
                                }
                            }}
                            className={clsx(sharedClasses.navButton, 'finish')}
                        >
                            {capitalizeAll(trans('label.finish'))}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};
